import { z } from 'zod';

/**
 * Schema de validação para Login
 */
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .email('Email inválido'),
  password: z
    .string()
    .min(1, 'Senha é obrigatória')
    .min(8, 'A senha deve ter pelo menos 8 caracteres'),
});

/**
 * Schema de validação para Registro
 */
export const registerSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome é obrigatório')
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .max(100, 'Nome muito longo'),
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .email('Email inválido'),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\(\d{2}\)\s?\d{4,5}-?\d{4}$/.test(val),
      'Telefone inválido. Use o formato (11) 99999-9999'
    ),
  role: z.enum(['user', 'owner'], {
    errorMap: () => ({ message: 'Selecione um tipo de conta' }),
  }),
  password: z
    .string()
    .min(1, 'Senha é obrigatória')
    .min(8, 'A senha deve ter pelo menos 8 caracteres')
    .max(50, 'Senha muito longa')
    .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
    .regex(/[0-9]/, 'A senha deve conter pelo menos um número')
    .regex(/[!@#$%^&*(),.?":{}|<>]/, 'A senha deve conter pelo menos um caractere especial (!@#$%^&*...)'),
  confirmPassword: z
    .string()
    .min(1, 'Confirme sua senha'),
  acceptTerms: z
    .boolean()
    .refine((val) => val === true, 'Você deve aceitar os termos de uso'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

/**
 * Schema de validação para Recuperação de Senha
 */
export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .email('Email inválido'),
});

/**
 * Schema de validação para Agendamento
 */
export const bookingSchema = z.object({
  service: z
    .string()
    .min(1, 'Selecione um serviço'),
  date: z
    .string()
    .min(1, 'Selecione uma data'),
  time: z
    .string()
    .min(1, 'Selecione um horário'),
  clientName: z
    .string()
    .min(1, 'Nome é obrigatório')
    .min(3, 'Nome deve ter pelo menos 3 caracteres'),
  clientPhone: z
    .string()
    .min(1, 'Telefone é obrigatório')
    .refine(
      (val) => /^\(\d{2}\)\s?\d{4,5}-?\d{4}$/.test(val),
      'Telefone inválido. Use o formato (11) 99999-9999'
    ),
  notes: z
    .string()
    .max(500, 'Observações muito longas (máximo 500 caracteres)')
    .optional(),
});

/**
 * Schema de validação para Adicionar Horário (Slot)
 */
export const addSlotSchema = z.object({
  date: z
    .string()
    .min(1, 'Data é obrigatória')
    .refine(
      (val) => {
        const selectedDate = new Date(val + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate >= today;
      },
      'A data não pode ser no passado'
    ),
  time: z
    .string()
    .min(1, 'Horário é obrigatório')
    .refine(
      (val) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(val),
      'Horário inválido. Use o formato HH:MM'
    ),
});

/**
 * Schema de validação para Criar Barbearia
 */
export const createBarbershopSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome é obrigatório')
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .max(100, 'Nome muito longo'),
  description: z
    .string()
    .min(1, 'Descrição é obrigatória')
    .min(10, 'Descrição deve ter pelo menos 10 caracteres')
    .max(500, 'Descrição muito longa (máximo 500 caracteres)'),
  city: z
    .string()
    .min(1, 'Cidade é obrigatória')
    .min(3, 'Cidade inválida'),
  bannerUrl: z
    .string()
    .url('URL inválida')
    .optional()
    .or(z.literal('')),
  services: z
    .array(z.string())
    .min(1, 'Adicione pelo menos um serviço'),
  isSponsored: z
    .boolean()
    .default(false),
});

/**
 * Schema de validação para Editar Perfil
 */
export const editProfileSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome é obrigatório')
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .max(100, 'Nome muito longo'),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\(\d{2}\)\s?\d{4,5}-?\d{4}$/.test(val),
      'Telefone inválido. Use o formato (11) 99999-9999'
    ),
  bio: z
    .string()
    .max(300, 'Bio muito longa (máximo 300 caracteres)')
    .optional(),
});

/**
 * Helper: Formatar telefone brasileiro
 */
export const formatPhone = (value) => {
  const cleaned = value.replace(/\D/g, '');
  
  if (cleaned.length <= 2) {
    return `(${cleaned}`;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  } else if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  } else {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  }
};

/**
 * Helper: Limpar formatação de telefone
 */
export const cleanPhone = (value) => {
  return value.replace(/\D/g, '');
};
